import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BarChart3, Heart, Megaphone, Calendar,
  Settings, Users, ShieldCheck,
  TrendingDown, TrendingUp, Layout, BookOpen, Wallet, MessageCircle,
  FileText, ClipboardList, Image as ImageIcon, PhoneCall, CreditCard,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { canAccessPath } from '../../lib/pageAccess';

interface SidebarProps {
  onLogout: () => void;
  open?: boolean;
  onClose?: () => void;
}

interface MenuItem {
  path: string;
  label: string;
  icon: React.ElementType;
  color: string;
  adminOnly?: boolean;
}

const NAV_ITEMS: MenuItem[] = [
  { path: '/',           label: 'דשבורד',                  icon: BarChart3,   color: 'text-blue-500' },
  { path: '/contacts',   label: 'אנשי קשר ו-CRM',          icon: Users,       color: 'text-indigo-500' },
  { path: '/tasks',      label: 'המשימות שלי',              icon: ClipboardList, color: 'text-amber-500' },
  { path: '/mailing',    label: 'דיוור ומיילים',            icon: Megaphone,   color: 'text-teal-500' },
  { path: '/whatsapp',   label: 'WhatsApp',                 icon: MessageCircle, color: 'text-green-500' },
  { path: '/telemarketing', label: 'המוקד שלי',             icon: PhoneCall,   color: 'text-orange-500' },
  { path: '/call-center',   label: 'ניהול מוקד',            icon: PhoneCall,   color: 'text-orange-600' },
  { path: '/campaigns',  label: 'קמפיינים',                 icon: Heart,       color: 'text-rose-500' },
  { path: '/tools',      label: 'דפי נחיתה וטפסים',        icon: Layout,      color: 'text-purple-500' },
  { path: '/gallery',    label: 'גלריית מדיה',              icon: ImageIcon,   color: 'text-pink-500' },
  { path: '/income',     label: 'הכנסות',                   icon: TrendingUp,  color: 'text-green-600' },
  { path: '/expenses',   label: 'הוצאות',                   icon: TrendingDown, color: 'text-red-500' },
  { path: '/finances',   label: 'פיננסים',                  icon: Wallet,      color: 'text-emerald-600' },
  { path: '/calendar',   label: 'לוח שנה ואירועים',           icon: Calendar,    color: 'text-sky-500' },
  { path: '/knowledge',  label: 'מרכז ידע',                  icon: BookOpen,    color: 'text-cyan-500' },
  { path: '/volunteers', label: 'מתנדבים',                   icon: Users,       color: 'text-green-500' },
  { path: '/volunteer-projects', label: 'פרויקטי התנדבות',   icon: Users,       color: 'text-lime-600' },
  { path: '/permissions', label: 'משתמשי מערכת',             icon: ShieldCheck, color: 'text-violet-500' },
  // Super-admin only
  { path: '/admin/quotes', label: 'הצעות מחיר',              icon: FileText,    color: 'text-orange-500', adminOnly: true },
  { path: '/admin/payment-forms', label: 'טפסי תשלום',       icon: CreditCard,  color: 'text-emerald-500', adminOnly: true },
  { path: '/admin/dev',    label: 'ניהול משימות',             icon: ClipboardList, color: 'text-blue-600', adminOnly: true },
];

export const Sidebar: React.FC<SidebarProps> = ({ onLogout: _, open, onClose }) => {
  const location = useLocation();
  const { user } = useAuth();

  const orgName = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}')?.orgName || null; } catch { return null; }
  }, []);

  const isSuperAdmin = user?.role === 'super_admin';

  // Every non-admin item is rendered; items the user lacks access to are shown
  // greyed-out and non-clickable (rather than hidden), so the user can see the
  // page exists but cannot enter it. Admin items show only for super_admin.
  const visibleItems = (() => {
    const base = NAV_ITEMS.filter(item => !item.adminOnly);
    if (isSuperAdmin) {
      return [...base, ...NAV_ITEMS.filter(item => item.adminOnly)];
    }
    return base;
  })();

  // super_admin sees everything; otherwise gate on role + allowedPages.
  const isAllowed = (path: string) =>
    isSuperAdmin || canAccessPath(path, user);

  // Close sidebar on navigation (mobile)
  React.useEffect(() => {
    if (onClose) onClose();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const sidebarContent = (
    <aside dir="rtl" className="w-64 lg:w-56 bg-white dark:bg-[#1A1A1A] h-full flex flex-col border-l border-gray-100 dark:border-gray-800 shadow-sm">

      {/* Logo */}
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex flex-col items-center">
        <img src="/amutech-logo.png" alt="Amutech" className="w-[140px] h-[140px] lg:w-[170px] lg:h-[170px] object-contain mix-blend-multiply dark:mix-blend-screen" />
        {orgName && (
          <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mt-1 truncate text-center w-full">
            עמותת {orgName}
          </p>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {visibleItems.map((item, idx) => {
          const Icon = item.icon;
          const active = location.pathname === item.path;
          const allowed = isAllowed(item.path);
          // Insert divider before first adminOnly item
          const prevItem = visibleItems[idx - 1];
          const showDivider = item.adminOnly && prevItem && !prevItem.adminOnly;
          return (
            <React.Fragment key={item.path}>
              {showDivider && (
                <div className="mx-3 my-2 border-t border-gray-100 dark:border-gray-800" />
              )}
              {allowed ? (
                <Link to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    active
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Icon size={17} className={active ? 'text-blue-600' : `${item.color} opacity-75`} />
                  <span className="flex-1">{item.label}</span>
                </Link>
              ) : (
                <div
                  aria-disabled="true"
                  title="אין לך הרשאת גישה לעמוד זה"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-300 dark:text-gray-600 opacity-50 cursor-not-allowed select-none"
                >
                  <Icon size={17} className="text-gray-300 dark:text-gray-600" />
                  <span className="flex-1">{item.label}</span>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </nav>

      {/* Settings shortcut */}
      <div className="p-2 border-t border-gray-100 dark:border-gray-800">
        {isAllowed('/settings') ? (
          <Link to="/settings"
            className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
          >
            <Settings size={16} className="opacity-60" />
            <span>הגדרות</span>
          </Link>
        ) : (
          <div
            aria-disabled="true"
            title="אין לך הרשאת גישה לעמוד זה"
            className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-gray-300 dark:text-gray-600 opacity-50 cursor-not-allowed select-none"
          >
            <Settings size={16} className="opacity-60" />
            <span>הגדרות</span>
          </div>
        )}
      </div>
    </aside>
  );

  return (
    <>
      {/* Desktop sidebar — always visible */}
      <div className="hidden lg:block flex-shrink-0">
        {sidebarContent}
      </div>

      {/* Mobile sidebar — overlay */}
      <div
        className={`fixed inset-0 z-50 lg:hidden transition-opacity duration-300 ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/40" onClick={onClose} />
        {/* Drawer — slides from right (RTL) */}
        <div
          className={`absolute top-0 right-0 h-full transition-transform duration-300 ease-out ${
            open ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {sidebarContent}
        </div>
      </div>
    </>
  );
};
